<template>
  <div class="contact-conversation--panel">
    <div v-if="!uiFlags.isFetching" class="contact-conversation__wrap">
      <div v-if="!previousConversations.length" class="no-label-message">
        <span>
          {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
        </span>
      </div>
      <div v-else class="contact-conversation--list">
        <div
          v-for="conv in prevConvItems"
          :key="conv.id"
          class="prev-conv"
          @click="navigateToConversation(conv)"
        >
          <span class="prev-dot" :style="{ background: conv.dotColor }" />
          <div class="prev-conv__body">
            <b class="text-truncate">{{ conv.title }}</b>
            <small>{{ conv.meta }}</small>
          </div>
        </div>
      </div>
    </div>
    <spinner v-else />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner';
import agentMixin from '../../../mixins/agentMixin';
import timeMixin from 'dashboard/mixins/time';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

export default {
  components: {
    Spinner,
  },
  mixins: [agentMixin, timeMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },
  computed: {
    conversations() {
      const currentId = this.currentUser?.id;
      const currentConversations = this.$store.getters[
        'contactConversations/getContactConversation'
      ](
        this.contactId,
        currentId,
        this.isLimitedAgent,
        !this.isPermissionSeeAllTeamConversation
      );

      return currentConversations;
    },
    previousConversations() {
      return this.conversations
        .map(conversation => {
          const live = this.$store.getters.getConversationById(conversation.id);
          return live ? { ...conversation, priority: live.priority } : conversation;
        })
        .sort((a, b) => b.id - a.id);
    },
    // Itens do histórico no estilo .prev-conv do protótipo
    prevConvItems() {
      const statusColors = {
        resolved: '#1f9d63',
        open: 'var(--color-woot)',
        pending: '#d98014',
        snoozed: '#7a5cf0',
      };
      const statusLabel = status =>
        this.$t(`CONTACT_PANEL.CONVERSATIONS.STATUS.${status}`);
      return this.previousConversations.map(c => {
        const msgs = c.messages || [];
        // Ignora mensagens de atividade (message_type 2) e template (3) no título
        const realMsgs = msgs.filter(
          m => m.message_type === 0 || m.message_type === 1
        );
        // Usa a PRIMEIRA mensagem real como "tópico" da conversa (igual ao protótipo)
        const topicMsg =
          realMsgs[0] || c.last_non_activity_message || {};
        const title =
          topicMsg.content ||
          this.$t('CONTACT_PANEL.CONVERSATIONS.UNTITLED', {
            id: c.display_id || c.id,
          });
        const timestamp = c.last_activity_at || c.created_at || topicMsg.created_at;
        const date = timestamp ? this.dynamicTime(timestamp) : '';
        const parts = [statusLabel(c.status), date];
        return {
          id: c.id,
          displayId: c.display_id || c.id,
          title,
          meta: parts.filter(Boolean).join(' · '),
          dotColor: statusColors[c.status] || 'var(--s-400)',
        };
      });
    },
    ...mapGetters({
      uiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
    }),
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.fetchConversations(newContactId);
      }
    },
  },
  mounted() {
    // Nota: O ContactPanel.vue pode chamar isso em paralelo
    // Se já estiver carregando via PromiseAll do pai, evita duplicar
    // Também evita recarregar se já tiver dados para este contactId (ex: abrir/fechar acordeão)
    if (!this.uiFlags.isFetching && this.conversations.length === 0) {
      this.fetchConversations(this.contactId);
    }
  },
  methods: {
    fetchConversations(contactId) {
      if (contactId) {
        this.$store.dispatch('contactConversations/get', contactId);
      }
    },
    navigateToConversation(conv) {
      this.$router.push(
        frontendURL(
          conversationUrl({ accountId: this.accountId, id: conv.displayId })
        )
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.no-label-message {
  margin-bottom: var(--space-normal);
  color: var(--s-500);
}

.prev-conv {
  display: flex;
  gap: var(--space-small);
  padding: var(--space-small) 0;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover .prev-conv__body b {
    color: var(--color-woot);
  }
}

.prev-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex: none;
}

.prev-conv__body {
  min-width: 0;

  b {
    display: block;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--s-800);
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: var(--font-size-mini);
    color: var(--s-400);
  }
}
</style>
